<script lang="ts">
/**
 * Optional capabilities of the preview, matching `ImagePreviewDialog`.
 *
 * - `zoom`: zoom in/out controls in the footer bar. Pages are re-rendered at the new
 *   scale, so text stays sharp instead of being upscaled, up to a ceiling on canvas size.
 * - `download`: download action in the header bar.
 *
 * Page controls are not opt-in: they only appear when the file has more than one page.
 */
export type PdfPreviewTool = 'zoom' | 'download';
</script>

<script setup lang="ts">
import { computed, nextTick, onScopeDispose, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import PreviewDialog from '@satellite/components/elements/preview/PreviewDialog.vue';
import { resolvePreviewSource } from '@satellite/components/elements/preview/previewSource';
import { downloadFile } from '@satellite/helpers/downloadFile';

import type { PreviewDialogSize } from '@satellite/components/elements/preview/PreviewDialog.vue';
import type { PreviewSource } from '@satellite/components/elements/preview/previewSource';

import type { Maybe } from '@nova/core';

import type { PDFDocumentLoadingTask, PDFDocumentProxy, RenderTask } from 'pdfjs-dist';

type PdfStatus = 'idle' | 'loading' | 'ready' | 'error';

const ZOOM_STEP = 1.5;
const MIN_ZOOM = 1;
const MAX_ZOOM = 4;

/**
 * Past 2x the pixels stop being visible and start being a problem: at a phone's 3x, an A4
 * page is ~15M pixels, and iOS hands back a blank canvas rather than allocating it.
 */
const MAX_DENSITY = 2;

/** A ceiling for one page whatever the zoom, for the same reason. */
const MAX_CANVAS_PIXELS = 8_000_000;

/** How far outside the viewport a page is still worth having drawn. */
const RENDER_MARGIN = '150%';

defineOptions({ name: 'PdfPreviewDialog' });

const {
  modelValue,
  src = '',
  fileName = '',
  tools = [],
  maxWidth = '1080px',
  closeOnScrim = true,
  size = 'adaptive',
  scrimOpacity = null,
  testId = 'pdf-preview'
} = defineProps<{
  /**
   * Whether the preview is open.
   */
  modelValue: boolean;
  /**
   * Where the document is read from: a URL, or a function resolving to one.
   */
  src?: PreviewSource;
  /**
   * Shown in the header bar and used as the downloaded file name.
   */
  fileName?: string;
  /**
   * Which controls this preview offers. Defaults to none, i.e. a plain preview.
   */
  tools?: PdfPreviewTool[];
  maxWidth?: string | number;
  closeOnScrim?: boolean;
  /**
   * How the dialog takes its size. `fullscreen` gives the pages the whole viewport to
   * draw into, rather than a column inset from it.
   */
  size?: PreviewDialogSize;
  /**
   * How far the scrim dims the page behind it, 0 to 1. Defaults to Vuetify's own.
   */
  scrimOpacity?: Maybe<number | string>;
  /**
   * Prefix for every `data-testid` this preview renders, e.g. `<testId>-pages`.
   */
  testId?: string;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  close: [];
  load: [pageCount: number];
  error: [error: unknown];
}>();

const { t } = useI18n();

const bodyRef = ref<HTMLElement | null>(null);
const pagesRef = ref<HTMLElement | null>(null);
const status = ref<PdfStatus>('idle');
const pageCount = ref(0);
const currentPage = ref(1);
const zoom = ref(MIN_ZOOM);
/** Where the document was actually read from, i.e. `src` once resolved. */
const sourceUrl = ref('');

const isOpen = computed({
  get: () => modelValue,
  set: value => emit('update:modelValue', value)
});

const isZoomEnabled = computed(() => tools.includes('zoom'));
/**
 * Offered only once there is something to save. A document that fails to *render* still
 * has a source, and stays downloadable — being able to keep a file the viewer cannot
 * draw is the point; a source that never resolved has nothing behind the button.
 */
const isDownloadEnabled = computed(() => tools.includes('download') && Boolean(sourceUrl.value));
const canZoomIn = computed(() => zoom.value < MAX_ZOOM);
const canZoomOut = computed(() => zoom.value > MIN_ZOOM);
const hasPages = computed(() => status.value === 'ready' && pageCount.value > 1);

/** Canvases are reused across zoom levels, so a re-render never rebuilds the list. */
const canvases: HTMLCanvasElement[] = [];
/** Page shapes, so a canvas holds its place in the scroll before anything is drawn on it. */
const pageRatios: number[] = [];

let visiblePages: IntersectionObserver | null = null;

let loadingTask: PDFDocumentLoadingTask | null = null;
let pdf: PDFDocumentProxy | null = null;
/** An object URL minted by a `src` function is ours to release with the document. */
let objectUrl: string | null = null;
let renderTasks: RenderTask[] = [];
// Guards against a render that is still running when another one starts.
let renderToken = 0;
// Same, for a load: every await in it is a chance for another one to have taken over.
let loadToken = 0;

function cancelRenders(): void {
  renderTasks.forEach(task => task.cancel());
  renderTasks = [];
}

async function releaseDocument(): Promise<void> {
  cancelRenders();
  visiblePages?.disconnect();
  visiblePages = null;
  canvases.length = 0;
  pageRatios.length = 0;
  pdf = null;

  await loadingTask?.destroy().catch(() => {});
  loadingTask = null;

  if (objectUrl) {
    URL.revokeObjectURL(objectUrl);
    objectUrl = null;
  }
}

/** The width a page occupies on screen; its height follows from the page's own shape. */
function pageWidth(): number {
  const container = pagesRef.value;

  return (container?.clientWidth || window.innerWidth) * zoom.value;
}

/**
 * Lays a canvas out at its final size without drawing anything, so pages keep their place
 * in the scroll whether or not they are currently rendered.
 */
function reserve(canvas: HTMLCanvasElement, index: number): void {
  const width = pageWidth();

  canvas.style.width = `${width}px`;
  canvas.style.height = `${width * (pageRatios[index] ?? 1)}px`;
}

/** Frees the backing store — hundreds of megabytes across a long document — but not the box. */
function release(canvas: HTMLCanvasElement): void {
  canvas.width = 0;
  canvas.height = 0;
  delete canvas.dataset.rendered;
}

/**
 * Draws one page at the width it occupies, at the device's pixel density, within a ceiling
 * on both. Panning a zoomed page is the body's own scrolling.
 */
async function drawPage(index: number): Promise<void> {
  const canvas = canvases[index];

  if (!pdf || !canvas || canvas.dataset.rendered === String(zoom.value)) {
    return;
  }

  const token = renderToken;
  const page = await pdf.getPage(index + 1);

  if (token !== renderToken) {
    return;
  }

  const width = pageWidth();
  const unscaled = page.getViewport({ scale: 1 });
  const density = Math.min(window.devicePixelRatio || 1, MAX_DENSITY);
  const fitted = (width / unscaled.width) * density;
  const pixels = unscaled.width * fitted * (unscaled.height * fitted);
  const scale =
    pixels > MAX_CANVAS_PIXELS ? fitted * Math.sqrt(MAX_CANVAS_PIXELS / pixels) : fitted;
  const viewport = page.getViewport({ scale });

  canvas.width = viewport.width;
  canvas.height = viewport.height;
  reserve(canvas, index);
  canvas.dataset.rendered = String(zoom.value);

  const task = page.render({ canvas, viewport });
  renderTasks.push(task);

  // A cancelled render rejects; the token is what tells the two apart.
  await task.promise.catch(() => {});

  if (token !== renderToken) {
    release(canvas);
  }
}

/**
 * Only pages at or near the viewport are drawn, and they are dropped again once well past
 * it: a long document otherwise holds every page in memory at once.
 */
function watchVisiblePages(): void {
  visiblePages?.disconnect();

  // Nothing to report visibility: draw the document in full rather than not at all.
  if (typeof IntersectionObserver === 'undefined') {
    canvases.forEach((_, index) => {
      drawPage(index);
    });

    return;
  }

  visiblePages = new IntersectionObserver(
    entries => {
      entries.forEach(entry => {
        const canvas = entry.target as HTMLCanvasElement;
        const index = canvases.indexOf(canvas);

        if (index === -1) {
          return;
        }

        if (entry.isIntersecting) {
          drawPage(index);
          return;
        }

        release(canvas);
      });
    },
    { root: bodyRef.value, rootMargin: RENDER_MARGIN }
  );

  canvases.forEach(canvas => visiblePages?.observe(canvas));
}

/** Everything on screen is redrawn at the new scale; the rest waits until it is scrolled to. */
function redrawPages(): void {
  const token = (renderToken += 1);

  cancelRenders();
  canvases.forEach((canvas, index) => {
    release(canvas);
    reserve(canvas, index);
  });

  // Re-observing reports what is on screen now, at the new page size.
  if (token === renderToken) {
    watchVisiblePages();
  }
}

/**
 * Gives every page a canvas of its own shape, so the document holds its full height in the
 * scroll before anything is drawn on it. Returns whether it ran to the end: a load that was
 * taken over partway through leaves the container to whichever one replaced it.
 */
async function buildPageCanvases(
  container: HTMLElement,
  pdfDocument: PDFDocumentProxy,
  isStale: () => boolean
): Promise<boolean> {
  container.replaceChildren();
  canvases.length = 0;
  pageRatios.length = 0;

  for (let pageNumber = 1; pageNumber <= pdfDocument.numPages; pageNumber += 1) {
    // Reading each page's shape up front is what lets the unrendered ones hold their place.
    // One page at a time on purpose: asking pdf.js for all of them at once would hold an
    // entire long document in memory, which is the very thing the lazy rendering avoids.
    // oxlint-disable-next-line no-await-in-loop
    const page = await pdfDocument.getPage(pageNumber);

    if (isStale()) {
      return false;
    }

    const { width, height } = page.getViewport({ scale: 1 });

    const canvas = window.document.createElement('canvas');
    canvas.className = 'pdf-preview__page';
    canvas.setAttribute('data-testid', `${testId}-page`);
    container.append(canvas);
    canvases.push(canvas);
    pageRatios.push(height / width);
    reserve(canvas, pageNumber - 1);
  }

  return true;
}

/**
 * iOS renders only the first page of a PDF in an iframe, with no scrolling, so every page is
 * drawn to its own canvas instead. pdf.js is imported here rather than at module scope to keep
 * it out of the bundle consumers download before they open a file.
 */
async function load(): Promise<void> {
  const token = (loadToken += 1);
  const isStale = () => token !== loadToken;

  status.value = 'loading';
  // Belongs to the document being replaced: saving it now would hand over the previous
  // file under the new one's name.
  sourceUrl.value = '';
  await releaseDocument();

  if (isStale()) {
    return;
  }

  try {
    const [pdfjs, workerSrc] = await Promise.all([
      import('pdfjs-dist'),
      import('pdfjs-dist/build/pdf.worker.min.mjs?url')
    ]);

    if (isStale()) {
      return;
    }

    pdfjs.GlobalWorkerOptions.workerSrc = workerSrc.default;

    const { url, isOwned } = await resolvePreviewSource(src);

    if (isStale()) {
      // Never reached `objectUrl`, so releasing it is this run's own to do.
      if (isOwned) {
        URL.revokeObjectURL(url);
      }

      return;
    }

    objectUrl = isOwned ? url : null;
    sourceUrl.value = url;

    // Published before it is awaited, so a load taking over from this one can destroy it.
    const task = pdfjs.getDocument({ url });
    loadingTask = task;

    const pdfDocument = await task.promise;

    if (isStale()) {
      // A load that took over before the line above ran has a task of its own to release.
      await task.destroy().catch(() => {});

      return;
    }

    pdf = pdfDocument;
    pageCount.value = pdfDocument.numPages;

    // The dialog mounts its contents lazily, so a preview opened at mount can have a
    // document before it has anywhere to put it. One tick is all that costs.
    if (!pagesRef.value) {
      await nextTick();

      if (isStale()) {
        return;
      }
    }

    const container = pagesRef.value;

    if (!container) {
      // Nowhere to draw the document: say so and let it go, rather than sit on a
      // spinner holding a document and its object URL for a preview that has none.
      status.value = 'error';
      emit('error', new Error('The preview has nowhere to draw the document.'));
      await releaseDocument();

      return;
    }

    const isComplete = await buildPageCanvases(container, pdfDocument, isStale);

    if (!isComplete) {
      return;
    }

    status.value = 'ready';
    watchVisiblePages();
    emit('load', pageCount.value);
  } catch (error) {
    if (isStale()) {
      return;
    }

    status.value = 'error';
    emit('error', error);
  }
}

function reset(): void {
  // A load still in flight is for a document no longer on screen: retire it too.
  loadToken += 1;
  pagesRef.value?.replaceChildren();
  status.value = 'idle';
  pageCount.value = 0;
  currentPage.value = 1;
  sourceUrl.value = '';
  zoom.value = MIN_ZOOM;
  releaseDocument();
}

function goToPage(pageNumber: number): void {
  const target = Math.min(Math.max(pageNumber, 1), pageCount.value);
  const canvas = canvases[target - 1];
  const body = bodyRef.value;

  if (!canvas || !body) {
    return;
  }

  currentPage.value = target;
  // The body is the canvases' offset parent, so their offsets are already its scroll positions.
  body.scrollTop = canvas.offsetTop;
}

/** The page in view is the first one still crossing the top of the viewport. */
function onScroll(): void {
  const body = bodyRef.value;

  if (!body || !canvases.length) {
    return;
  }

  const top = body.scrollTop;
  const visible = canvases.findIndex(canvas => canvas.offsetTop + canvas.offsetHeight > top);

  currentPage.value = visible === -1 ? pageCount.value : visible + 1;
}

function zoomIn(): void {
  zoom.value = Math.min(zoom.value * ZOOM_STEP, MAX_ZOOM);
}

function zoomOut(): void {
  zoom.value = Math.max(zoom.value / ZOOM_STEP, MIN_ZOOM);
}

async function download(): Promise<void> {
  await downloadFile(sourceUrl.value, fileName);
}

watch(
  () => [isOpen.value, src] as const,
  ([open]) => {
    if (open && src) {
      load();
      return;
    }

    reset();
  },
  // Also covers being mounted already open, not just opened later.
  { immediate: true }
);

watch(zoom, redrawPages);

onScopeDispose(() => {
  releaseDocument();
});
</script>

<template>
  <preview-dialog
    v-model="isOpen"
    :file-name="fileName"
    :max-width="maxWidth"
    :close-on-scrim="closeOnScrim"
    :size="size"
    :scrim-opacity="scrimOpacity"
    :show-download="isDownloadEnabled"
    :is-loading="status === 'loading'"
    fills-viewport
    :test-id="testId"
    @close="emit('close')"
    @download="download"
    @previous="goToPage(currentPage - 1)"
    @next="goToPage(currentPage + 1)">
    <div ref="bodyRef" class="pdf-preview__body" @scroll="onScroll">
      <v-progress-circular
        v-if="status === 'loading'"
        class="pdf-preview__status"
        indeterminate
        color="primary"
        :data-testid="`${testId}-loader`" />

      <div v-if="status === 'error'" class="pdf-preview__status" :data-testid="`${testId}-error`">
        <!-- @slot Replaces the failed-to-render placeholder. -->
        <slot name="error">
          <v-icon icon="mdi-file-alert-outline" size="48" />
        </slot>
      </div>

      <!-- Always laid out: measuring it while hidden gives a width of 0, and blank canvases. -->
      <div ref="pagesRef" class="pdf-preview__pages" :data-testid="`${testId}-pages`"></div>
    </div>

    <template v-if="isZoomEnabled || hasPages" #footer>
      <v-btn
        v-if="isZoomEnabled"
        icon="mdi-magnify-minus-outline"
        variant="text"
        density="comfortable"
        :disabled="!canZoomOut"
        :aria-label="t('global.basicTerms.zoomOut')"
        :data-testid="`${testId}-zoom-out`"
        @click="zoomOut" />

      <template v-if="hasPages">
        <v-btn
          icon="mdi-chevron-left"
          variant="text"
          density="comfortable"
          :disabled="currentPage <= 1"
          :aria-label="t('global.basicTerms.previous')"
          :data-testid="`${testId}-previous`"
          @click="goToPage(currentPage - 1)" />

        <span class="typography-body-md" :data-testid="`${testId}-page-count`">
          {{ t('global.basicTerms.pageOfTotal', { current: currentPage, total: pageCount }) }}
        </span>

        <v-btn
          icon="mdi-chevron-right"
          variant="text"
          density="comfortable"
          :disabled="currentPage >= pageCount"
          :aria-label="t('global.basicTerms.next')"
          :data-testid="`${testId}-next`"
          @click="goToPage(currentPage + 1)" />
      </template>

      <v-btn
        v-if="isZoomEnabled"
        icon="mdi-magnify-plus-outline"
        variant="text"
        density="comfortable"
        :disabled="!canZoomIn"
        :aria-label="t('global.basicTerms.zoomIn')"
        :data-testid="`${testId}-zoom-in`"
        @click="zoomIn" />
    </template>
  </preview-dialog>
</template>

<style lang="scss" scoped>
.pdf-preview__body {
  position: relative;
  // Takes whatever height the bars leave, and scrolls the rest.
  flex: 1 1 auto;
  min-height: 0;
  padding: 8px;
  overflow: auto;
  background-color: $color-neutral-20;
  -webkit-overflow-scrolling: touch;
}

/* Padding sits on the body, so the pages container measures exactly the page width. */
.pdf-preview__pages {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}

.pdf-preview__status {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

/* Sized in script: a canvas dropped to free memory has no intrinsic size to lay out by. */
:deep(.pdf-preview__page) {
  flex-shrink: 0;
  background-color: $color-neutral-0;
}
</style>
